import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { collection, getDocs, query, where, addDoc, updateDoc, deleteDoc, doc, Timestamp, writeBatch } from 'firebase/firestore';
import { db, functions, functionsUS } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ProductOption } from '../types';
import MediaUpload from '../components/MediaUpload';
import { getLocalizedName, getLocalizedChoiceName } from '../utils/localization';

import { clamp } from '../utils/numberGuard';
const ProductOptions: React.FC = () => {
  const { shop } = useAuth();
  const { language } = useLanguage();
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingOption, setEditingOption] = useState<ProductOption | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductOption | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    nameTh: '',
    nameZh: '',
    nameKo: '',
    type: 'single' as 'single' | 'multiple',
    required: false,
    displayStatus: 'available' as 'available' | 'hidden' | 'soldout',
    choices: [
      { id: '1', code: '', name: '', nameEn: '', nameTh: '', nameZh: '', nameKo: '', price: 0, imageUrl: '', mediaType: 'image' as 'image' | 'video' }
    ],
    isActive: true,
  });

  useEffect(() => {
    if (shop) {
      fetchOptions();
    }
  }, [shop]);

  const fetchOptions = async () => {
    if (!shop) return;

    try {
      const q = query(
        collection(db, 'productOptions'),
        where('shopId', '==', shop.id)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as ProductOption[];
      setOptions(data);
    } catch (error) {
      console.error('オプション取得エラー:', error);
      setError('オプションの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (option?: ProductOption) => {
    if (option) {
      setEditingOption(option);
      setFormData({
        name: option.name,
        nameEn: option.nameEn || '',
        nameTh: option.nameTh || '',
        nameZh: option.nameZh || '',
        nameKo: option.nameKo || '',
        type: option.type,
        required: option.required,
        displayStatus: option.displayStatus || 'available',
        choices: option.choices.map((c, idx) => ({
          id: String(idx + 1),
          code: c.code || '',
          name: c.name,
          nameEn: c.nameEn || '',
          nameTh: c.nameTh || '',
          nameZh: c.nameZh || '',
          nameKo: c.nameKo || '',
          price: c.price,
          imageUrl: c.imageUrl || '',
          mediaType: c.mediaType || 'image',
        })),
        isActive: option.isActive,
      });
    } else {
      setEditingOption(null);
      setFormData({
        name: '',
        nameEn: '',
        nameTh: '',
        nameZh: '',
        nameKo: '',
        type: 'single',
        required: false,
        displayStatus: 'available',
        choices: [
          { id: '1', code: '', name: '', nameEn: '', nameTh: '', nameZh: '', nameKo: '', price: 0, imageUrl: '', mediaType: 'image' as 'image' | 'video' }
        ],
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOption(null);
    setError(null);
  };

  const handleAddChoice = () => {
    setFormData({
      ...formData,
      choices: [
        ...formData.choices,
        { id: String(formData.choices.length + 1), code: '', name: '', nameEn: '', nameTh: '', nameZh: '', nameKo: '', price: 0, imageUrl: '', mediaType: 'image' as 'image' | 'video' }
      ]
    });
  };

  const handleRemoveChoice = (id: string) => {
    if (formData.choices.length <= 1) {
      setError('選択肢は最低1つ必要です');
      return;
    }
    setFormData({
      ...formData,
      choices: formData.choices.filter(c => c.id !== id)
    });
  };

  const handleChoiceChange = (id: string, field: string, value: string | number) => {
    setFormData({
      ...formData,
      choices: formData.choices.map(c =>
        c.id === id ? { ...c, [field]: value } : c
      )
    });
  };

  const handleSubmit = async () => {
    if (!shop || !formData.name) {
      setError('オプション名（日本語）は必須です');
      return;
    }

    if (formData.choices.some(c => !c.name)) {
      setError('すべての選択肢に名前（日本語）を入力してください');
      return;
    }

    try {
      const now = Timestamp.now();
      const optionData = {
        shopId: shop.id,
        name: formData.name,
        nameEn: formData.nameEn || null,
        nameTh: formData.nameTh || null,
        nameZh: formData.nameZh || null,
        nameKo: formData.nameKo || null,
        type: formData.type,
        required: formData.required,
        displayStatus: formData.displayStatus,
        choices: formData.choices.map(c => ({
          id: c.id,
          code: c.code || null,
          name: c.name,
          nameEn: c.nameEn || null,
          nameTh: c.nameTh || null,
          nameZh: c.nameZh || null,
          nameKo: c.nameKo || null,
          price: clamp(c.price, 0, Number.MAX_SAFE_INTEGER),
          imageUrl: c.imageUrl || null,
          mediaType: c.mediaType || null,
        })),
        isActive: formData.isActive,
        updatedAt: now,
      };

      if (editingOption) {
        // 更新
        await updateDoc(doc(db, 'productOptions', editingOption.id), optionData);
        setSuccess('オプションを更新しました');
      } else {
        // 新規作成
        await addDoc(collection(db, 'productOptions'), {
          ...optionData,
          createdAt: now,
        });
        setSuccess('オプションを追加しました');
      }

      handleCloseDialog();
      fetchOptions();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('オプション保存エラー:', error);
      setError('オプションの保存に失敗しました');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteDoc(doc(db, 'productOptions', deleteTarget.id));
      setSuccess('オプションを削除しました');
      setOpenDeleteDialog(false);
      setDeleteTarget(null);
      fetchOptions();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('オプション削除エラー:', error);
      setError('オプションの削除に失敗しました');
    }
  };

  const handleBatchTranslate = async () => {
    if (!shop || options.length === 0) return;

    const confirmed = window.confirm(
      `全${options.length}件のオプションを一括翻訳しますか？\n` +
      `※既に翻訳済みの項目はスキップされます\n` +
      `※処理には数分かかる場合があります`
    );

    if (!confirmed) return;

    setTranslating(true);
    setError(null);
    setTranslationProgress('翻訳準備中...');

    try {
      // タイムアウトを10分に設定
      const translateFn = httpsCallable(functions, 'translateMenuItems', { timeout: 600000 });

      const sourceLanguage = language || 'ja';
      const allLanguages: ('ja' | 'en' | 'th' | 'zh-TW' | 'ko')[] = ['ja', 'en', 'th', 'zh-TW', 'ko'];
      const targetLanguages = allLanguages.filter(lang => lang !== sourceLanguage);

      // オプション名と選択肢名を翻訳対象に含める
      const items = options.flatMap(option => {
        const results: any[] = [
          {
            type: 'option',
            id: option.id,
            name: option.name,
            nameEn: option.nameEn,
            nameTh: option.nameTh,
            nameZhTw: option.nameZhTw,
            nameKo: option.nameKo,
          }
        ];

        // 各選択肢も翻訳対象に追加
        option.choices.forEach((choice, idx) => {
          results.push({
            type: 'optionChoice',
            id: `${option.id}_choice_${idx}`,
            optionId: option.id,
            choiceIndex: idx,
            name: choice.name,
            nameEn: choice.nameEn,
            nameTh: choice.nameTh,
            nameZhTw: choice.nameZhTw,
            nameKo: choice.nameKo,
          });
        });

        return results;
      });

      setTranslationProgress(`翻訳中... (${items.length}件)`);

      const result = await translateFn({
        shopName: shop.shopName,
        businessTypeCode: shop.businessTypeCode,
        sourceLanguage,
        targetLanguages,
        items,
        batchSize: 30,
        forceRetranslate: true,
      });

      const data = result.data as { translations: any[]; skipped: number };

      setTranslationProgress('データベースを更新中...');

      // 翻訳結果を整理
      const optionTranslations = new Map<string, any>();
      const choiceTranslations = new Map<string, Map<number, any>>();

      data.translations.forEach((translation: any) => {
        if (translation.type === 'option') {
          optionTranslations.set(translation.id, translation);
        } else if (translation.type === 'optionChoice') {
          if (!choiceTranslations.has(translation.optionId)) {
            choiceTranslations.set(translation.optionId, new Map());
          }
          choiceTranslations.get(translation.optionId)!.set(translation.choiceIndex, translation);
        }
      });

      // Batch Writeで一括更新
      const batch = writeBatch(db);
      let updateCount = 0;

      options.forEach(option => {
        const optionTranslation = optionTranslations.get(option.id);
        const optionChoiceTranslations = choiceTranslations.get(option.id);

        const updateData: any = {
          updatedAt: Timestamp.now(),
        };

        // オプション名の翻訳を更新
        if (optionTranslation) {
          if (optionTranslation.name) updateData.name = optionTranslation.name;
          if (optionTranslation.nameEn) updateData.nameEn = optionTranslation.nameEn;
          if (optionTranslation.nameTh) updateData.nameTh = optionTranslation.nameTh;
          if (optionTranslation.nameZhTw) updateData.nameZhTw = optionTranslation.nameZhTw;
          if (optionTranslation.nameKo) updateData.nameKo = optionTranslation.nameKo;
        }

        // 選択肢の翻訳を更新
        if (optionChoiceTranslations && optionChoiceTranslations.size > 0) {
          updateData.choices = option.choices.map((choice, idx) => {
            const choiceTranslation = optionChoiceTranslations.get(idx);
            if (choiceTranslation) {
              return {
                ...choice,
                name: choiceTranslation.name || choice.name,
                nameEn: choiceTranslation.nameEn || choice.nameEn,
                nameTh: choiceTranslation.nameTh || choice.nameTh,
                nameZhTw: choiceTranslation.nameZhTw || choice.nameZhTw,
                nameKo: choiceTranslation.nameKo || choice.nameKo,
              };
            }
            return choice;
          });
        }

        // 更新内容があれば適用
        if (Object.keys(updateData).length > 1) { // updatedAt以外のフィールドがある場合
          const optionRef = doc(db, 'productOptions', option.id);
          batch.update(optionRef, updateData);
          updateCount++;
        }
      });

      await batch.commit();

      setSuccess(
        `翻訳完了: ${updateCount}件を更新しました` +
        (data.skipped > 0 ? ` (${data.skipped}件はスキップ)` : '')
      );
      setTimeout(() => setSuccess(null), 5000);

      await fetchOptions();
    } catch (error: any) {
      console.error('一括翻訳エラー:', error);
      setError(`翻訳に失敗しました: ${error.message || '不明なエラー'}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setTranslating(false);
      setTranslationProgress('');
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>読み込み中...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">商品オプション管理</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={translating ? <span>⏳</span> : <TranslateIcon />}
            onClick={handleBatchTranslate}
            disabled={translating || options.length === 0}
          >
            {translating ? translationProgress : '一括翻訳 (AI)'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            オプション追加
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>オプション名</TableCell>
                <TableCell>タイプ</TableCell>
                <TableCell>必須</TableCell>
                <TableCell>選択肢数</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {options.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    オプションが登録されていません
                  </TableCell>
                </TableRow>
              ) : (
                options.map((option) => (
                  <TableRow key={option.id}>
                    <TableCell>
                      <Typography variant="body2">{getLocalizedName(option, language)}</Typography>
                      {language === 'ja' && option.nameEn && (
                        <Typography variant="caption" color="text.secondary">
                          {option.nameEn}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={option.type === 'single' ? '単一選択' : '複数選択'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {option.required ? '必須' : '任意'}
                    </TableCell>
                    <TableCell>{option.choices.length}個</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={
                            option.displayStatus === 'available' ? '表示中' :
                            option.displayStatus === 'soldout' ? '売り切れ' :
                            '非表示'
                          }
                          color={
                            option.displayStatus === 'available' ? 'success' :
                            option.displayStatus === 'soldout' ? 'warning' :
                            'default'
                          }
                          size="small"
                        />
                        {!option.isActive && (
                          <Chip
                            label="無効"
                            color="default"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(option)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeleteTarget(option);
                          setOpenDeleteDialog(true);
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 編集/追加ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingOption ? 'オプション編集' : 'オプション追加'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="オプション名"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              placeholder="例: サイズ"
            />

            {/* 多言語入力 */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                label="英語 (EN)"
                value={formData.nameEn}
                onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                size="small"
                sx={{ flex: 1, minWidth: 140 }}
                placeholder="Size"
              />
              <TextField
                label="タイ語 (TH)"
                value={formData.nameTh}
                onChange={(e) => setFormData({ ...formData, nameTh: e.target.value })}
                size="small"
                sx={{ flex: 1, minWidth: 140 }}
                placeholder="ขนาด"
              />
              <TextField
                label="中国語 (ZH)"
                value={formData.nameZh}
                onChange={(e) => setFormData({ ...formData, nameZh: e.target.value })}
                size="small"
                sx={{ flex: 1, minWidth: 140 }}
                placeholder="尺寸"
              />
              <TextField
                label="韓国語 (KO)"
                value={formData.nameKo}
                onChange={(e) => setFormData({ ...formData, nameKo: e.target.value })}
                size="small"
                sx={{ flex: 1, minWidth: 140 }}
                placeholder="크기"
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel>選択タイプ</InputLabel>
              <Select
                value={formData.type}
                label="選択タイプ"
                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'single' | 'multiple' })}
              >
                <MenuItem value="single">単一選択（1つだけ選択可）</MenuItem>
                <MenuItem value="multiple">複数選択（複数選択可）</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.required}
                  onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                />
              }
              label="必須オプション"
            />

            <Typography variant="subtitle2" sx={{ mt: 2 }}>表示ステータス</Typography>

            <ToggleButtonGroup
              value={formData.displayStatus}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) {
                  setFormData({ ...formData, displayStatus: newValue });
                }
              }}
              fullWidth
              sx={{ mb: 2 }}
            >
              <ToggleButton value="available" color="success">
                表示中
              </ToggleButton>
              <ToggleButton value="soldout" color="warning">
                売り切れ
              </ToggleButton>
              <ToggleButton value="hidden" color="standard">
                非表示
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider />

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">選択肢</Typography>
                <Button
                  size="small"
                  startIcon={<AddCircleIcon />}
                  onClick={handleAddChoice}
                >
                  選択肢を追加
                </Button>
              </Box>

              <List>
                {formData.choices.map((choice, index) => (
                  <ListItem key={choice.id} sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, pb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                      <TextField
                        label="コード"
                        value={choice.code}
                        onChange={(e) => handleChoiceChange(choice.id, 'code', e.target.value)}
                        size="small"
                        sx={{ width: 80 }}
                        placeholder="001"
                        inputProps={{ maxLength: 10 }}
                      />
                      <TextField
                        label={`選択肢${index + 1}`}
                        value={choice.name}
                        onChange={(e) => handleChoiceChange(choice.id, 'name', e.target.value)}
                        required
                        fullWidth
                        size="small"
                        placeholder="例: Sサイズ"
                      />
                      <TextField
                        label="追加料金"
                        value={choice.price}
                        onChange={(e) => {
                          const value = e.target.value;
                          // 数値または空文字列のみ許可
                          if (value === '' || /^\d+$/.test(value)) {
                            handleChoiceChange(choice.id, 'price', value === '' ? 0 : Number(value));
                          }
                        }}
                        size="small"
                        sx={{ width: 150 }}
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 0.5 }}>¥</Typography>
                        }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveChoice(choice.id)}
                        disabled={formData.choices.length <= 1}
                      >
                        <RemoveCircleIcon />
                      </IconButton>
                    </Box>
                    {/* 選択肢の多言語入力 */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pl: 1 }}>
                      <TextField
                        label="EN"
                        value={choice.nameEn}
                        onChange={(e) => handleChoiceChange(choice.id, 'nameEn', e.target.value)}
                        size="small"
                        sx={{ flex: 1, minWidth: 100 }}
                      />
                      <TextField
                        label="TH"
                        value={choice.nameTh}
                        onChange={(e) => handleChoiceChange(choice.id, 'nameTh', e.target.value)}
                        size="small"
                        sx={{ flex: 1, minWidth: 100 }}
                      />
                      <TextField
                        label="ZH"
                        value={choice.nameZh}
                        onChange={(e) => handleChoiceChange(choice.id, 'nameZh', e.target.value)}
                        size="small"
                        sx={{ flex: 1, minWidth: 100 }}
                      />
                      <TextField
                        label="KO"
                        value={choice.nameKo}
                        onChange={(e) => handleChoiceChange(choice.id, 'nameKo', e.target.value)}
                        size="small"
                        sx={{ flex: 1, minWidth: 100 }}
                      />
                    </Box>
                    {shop?.displaySettings?.showOptionImages !== false && (
                      <MediaUpload
                        currentMediaUrl={choice.imageUrl}
                        currentMediaType={choice.mediaType}
                        onMediaUploaded={(url, type) => {
                          handleChoiceChange(choice.id, 'imageUrl', url);
                          handleChoiceChange(choice.id, 'mediaType', type);
                        }}
                        onMediaDeleted={() => {
                          handleChoiceChange(choice.id, 'imageUrl', '');
                          handleChoiceChange(choice.id, 'mediaType', 'image');
                        }}
                        storagePath={`product-options/${editingOption?.id || 'new'}/${choice.id}`}
                        label={`選択肢${index + 1}のメディア`}
                      />
                    )}
                    {index < formData.choices.length - 1 && <Divider sx={{ mt: 1 }} />}
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingOption ? '更新' : '追加'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>オプションを削除</DialogTitle>
        <DialogContent>
          <Typography>
            オプション「{deleteTarget?.name}」を削除してもよろしいですか？
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            ※ このオプションを使用している商品に影響があります。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>キャンセル</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProductOptions;
